import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, TrendingUp, Calculator, Target, ChartBar } from 'lucide-react';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';

const SportsBettingGuide = () => {
  const [expandedSections, setExpandedSections] = React.useState({});

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Structured data for SEO
  const structuredData = {
    article: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Complete Sports Betting Guide with AI - Learn Betting Strategies | PickStar AI",
      "description": "Master sports betting with AI-powered insights. Learn betting basics, bankroll management, winning strategies, and how PickStar AI gives you an edge.",
      "keywords": "sports betting guide, AI betting, betting strategies, bankroll management, sports betting tips, betting odds explained, AI predictions, PickStar AI, sports betting for beginners, professional betting strategies",
      "author": {
        "@type": "Organization",
        "name": "PickStar AI",
        "url": "https://pickstarai.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "PickStar AI",
        "logo": {
          "@type": "ImageObject",
          "url": "https://pickstarai.com/logo.png"
        }
      },
    "datePublished": "2025-08-22",
    "dateModified": new Date().toISOString(),
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://pickstarai.com/resources/sports-betting-guide"
      },
      "image": "https://pickstarai.com/images/betting-guide-hero.jpg"
    },
    howTo: {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Use AI for Sports Betting",
      "description": "Step-by-step guide to using PickStar AI for making smarter sports betting decisions",
      "step": [
        {
          "@type": "HowToStep",
          "name": "Ask Specific Questions",
          "text": "Ask PickStar AI specific questions about games, teams, or betting opportunities"
        },
        {
          "@type": "HowToStep",
          "name": "Review AI Analysis",
          "text": "Review the detailed AI analysis including statistics, trends, and confidence levels"
        },
        {
          "@type": "HowToStep",
          "name": "Compare with Your Research",
          "text": "Use AI predictions alongside your own knowledge and other reliable sources"
        },
        {
          "@type": "HowToStep",
          "name": "Place Smart Bets",
          "text": "Follow bankroll management rules and bet responsibly based on combined insights"
        }
      ]
    },
    faq: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What makes PickStar AI better than ChatGPT for sports betting?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "PickStar AI is built by machine learning engineers who are also professional sports bettors. Our specialized context engineering understands betting nuances that general AI models miss, providing superior predictions and real-time market intelligence."
          }
        },
        {
          "@type": "Question",
          "name": "What is the 1-3% bankroll management rule?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The 1-3% rule means never risking more than 1-3% of your total bankroll on a single bet. For example, with a $1,000 bankroll, your maximum bet should be $10-$30 per wager. This protects you from devastating losses."
          }
        },
        {
          "@type": "Question",
          "name": "What are the main types of sports bets?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The main bet types are: Moneyline (pick the winner), Spread (bet on margin of victory), Over/Under (total points scored), Props (specific player/game events), and Parlays (multiple bets combined for higher payouts)."
          }
        },
        {
          "@type": "Question",
          "name": "How do American odds work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "American odds show potential profit. Positive odds (+150) mean bet $100 to win $150. Negative odds (-150) mean bet $150 to win $100. The favorites have negative odds, underdogs have positive odds."
          }
        }
      ]
    }
  };

  return (
    <>
      <Helmet>
        <title>Sports Betting Guide 2024 - AI-Powered Betting Strategies | PickStar AI</title>
        <meta name="description" content="Master sports betting with AI in 2024. Complete guide covering betting basics, bankroll management, winning strategies, and how to use PickStar AI for smarter bets." />
        <meta name="keywords" content="sports betting guide, AI sports betting, betting strategies 2024, bankroll management, sports betting tips, betting odds explained, AI predictions, PickStar AI guide, NFL betting, NBA betting, MLB betting" />
        <link rel="canonical" href="https://pickstarai.com/resources/sports-betting-guide" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Complete Sports Betting Guide with AI - PickStar AI" />
        <meta property="og:description" content="Learn how to bet smarter with AI-powered insights. Master betting basics, strategies, and bankroll management." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pickstarai.com/resources/sports-betting-guide" />
        <meta property="og:image" content="https://pickstarai.com/images/betting-guide-og.jpg" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sports Betting Guide with AI | PickStar AI" />
        <meta name="twitter:description" content="Master sports betting with AI-powered predictions and strategies" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData.article)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredData.howTo)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredData.faq)}
        </script>
      </Helmet>
      
      <SEOHead 
        customTitle="Sports Betting Guide - Learn AI-Powered Betting | PickStar AI"
        customDescription="Complete guide to sports betting with AI. Learn betting basics, strategies, bankroll management, and how to use AI predictions for smarter bets."
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        {/* Header */}
        <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-12 sm:py-16 px-4" role="banner" aria-label="Page header">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="w-8 h-8 text-emerald-400" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
                Sports Betting Guide
              </h1>
            </div>
            <p className="text-lg sm:text-xl text-gray-300 mb-8">
              Master the fundamentals of sports betting with AI-powered insights
            </p>
          </div>
        </section>

        {/* Main Content */}
        <main className="px-4 pb-16" role="main" aria-label="Main content">
          <div className="max-w-4xl mx-auto space-y-12">
            
            {/* Introduction */}
            <section id="introduction" className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="intro-heading">
              <h2 id="intro-heading" className="text-xl sm:text-2xl font-bold text-white mb-4">Introduction to AI Sports Betting</h2>
              <p className="text-gray-300 mb-4">
                Welcome to the future of sports betting. PickStar AI combines cutting-edge artificial intelligence 
                with comprehensive sports data to give you an unprecedented edge in your betting decisions.
              </p>
              <p className="text-gray-300">
                This guide will teach you the fundamentals of sports betting, how to leverage AI predictions, 
                and strategies to maximize your success while maintaining responsible gambling practices.
              </p>
            </section>

            {/* Betting Basics */}
            <section id="betting-basics" className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="basics-heading">
              <div className="flex items-center gap-3 mb-6">
                <Target className="w-6 h-6 text-emerald-400" />
                <h2 id="basics-heading" className="text-xl sm:text-2xl font-bold text-white">Betting Basics</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-emerald-300 mb-2">Understanding Odds</h3>
                  <p className="text-gray-300 mb-3">
                    Odds represent the probability of an outcome and determine your potential payout:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li><strong className="text-white">American Odds:</strong> +150 means bet $100 to win $150; -150 means bet $150 to win $100</li>
                    <li><strong className="text-white">Decimal Odds:</strong> 2.50 means total return of $2.50 for every $1 bet</li>
                    <li><strong className="text-white">Fractional Odds:</strong> 3/2 means win $3 for every $2 bet</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-emerald-300 mb-2">Common Bet Types</h3>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li><strong className="text-white">Moneyline:</strong> Pick the winner of the game</li>
                    <li><strong className="text-white">Spread:</strong> Bet on the margin of victory</li>
                    <li><strong className="text-white">Over/Under:</strong> Bet on total points scored</li>
                    <li><strong className="text-white">Props:</strong> Bet on specific player or game events</li>
                    <li><strong className="text-white">Parlays:</strong> Combine multiple bets for higher payouts</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* AI Advantage */}
            <section id="ai-advantage" className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="ai-heading">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="w-6 h-6 text-emerald-400" />
                <h2 id="ai-heading" className="text-xl sm:text-2xl font-bold text-white">The PickStar AI Advantage</h2>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-300">
                  Unlike generic AI like ChatGPT, PickStar AI is built by highly trained machine learning engineers who are also 
                  sports betting experts with years of professional experience. Our proprietary context engineering makes us the industry leader:
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-black/30 rounded-lg p-4 border border-emerald-500/20">
                    <h4 className="font-semibold text-emerald-300 mb-2">Expert Context Engineering</h4>
                    <p className="text-gray-300 text-sm">
                      Fine-tuned by ML engineers who are also professional sports bettors - the perfect combination of technical and domain expertise
                    </p>
                  </div>
                  
                  <div className="bg-black/30 rounded-lg p-4 border border-emerald-500/20">
                    <h4 className="font-semibold text-emerald-300 mb-2">Superior to ChatGPT</h4>
                    <p className="text-gray-300 text-sm">
                      Our specialized context engineering understands betting nuances that general AI models miss completely
                    </p>
                  </div>
                  
                  <div className="bg-black/30 rounded-lg p-4 border border-emerald-500/20">
                    <h4 className="font-semibold text-emerald-300 mb-2">Real-Time Market Intelligence</h4>
                    <p className="text-gray-300 text-sm">
                      Adjusts predictions based on latest news, lineups, and betting market movements
                    </p>
                  </div>
                  
                  <div className="bg-black/30 rounded-lg p-4 border border-emerald-500/20">
                    <h4 className="font-semibold text-emerald-300 mb-2">Professional-Grade Analysis</h4>
                    <p className="text-gray-300 text-sm">
                      Trained on strategies from successful professional bettors, not amateur betting patterns
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Bankroll Management */}
            <section id="bankroll-management" className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="bankroll-heading">
              <div className="flex items-center gap-3 mb-6">
                <Calculator className="w-6 h-6 text-emerald-400" />
                <h2 id="bankroll-heading" className="text-xl sm:text-2xl font-bold text-white">Bankroll Management</h2>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-300">
                  Proper bankroll management is crucial for long-term success:
                </p>
                
                <div className="bg-gradient-to-r from-emerald-500/10 to-violet-500/10 rounded-lg p-6 border border-emerald-500/20">
                  <h3 className="text-lg font-semibold text-white mb-3">The 1-3% Rule</h3>
                  <p className="text-gray-300 mb-3">
                    Never risk more than 1-3% of your total bankroll on a single bet. This protects you from 
                    devastating losses and allows you to weather losing streaks.
                  </p>
                  <div className="bg-black/30 rounded p-3">
                    <p className="text-sm text-gray-400">
                      <strong className="text-emerald-300">Example:</strong> With a $1,000 bankroll, your maximum 
                      bet should be $10-$30 per wager.
                    </p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-white">Key Principles:</h3>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>Set a dedicated betting bankroll separate from living expenses</li>
                    <li>Track all bets and maintain detailed records</li>
                    <li>Adjust bet sizes based on bankroll growth or decline</li>
                    <li>Never chase losses with larger bets</li>
                    <li>Take breaks during losing streaks</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Strategy Tips */}
            <section id="winning-strategies" className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="strategy-heading">
              <div className="flex items-center gap-3 mb-6">
                <ChartBar className="w-6 h-6 text-emerald-400" />
                <h2 id="strategy-heading" className="text-xl sm:text-2xl font-bold text-white">Winning Strategies</h2>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-emerald-300 mb-3">Do's</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">✓</span>
                      <span className="text-gray-300">Shop for the best odds across multiple sportsbooks</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">✓</span>
                      <span className="text-gray-300">Focus on sports and leagues you understand</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">✓</span>
                      <span className="text-gray-300">Use AI predictions as part of your research</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">✓</span>
                      <span className="text-gray-300">Keep detailed records of all bets</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">✓</span>
                      <span className="text-gray-300">Set win/loss limits for each session</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-red-400 mb-3">Don'ts</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 mt-1">✗</span>
                      <span className="text-gray-300">Bet with money you can't afford to lose</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 mt-1">✗</span>
                      <span className="text-gray-300">Chase losses with bigger bets</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 mt-1">✗</span>
                      <span className="text-gray-300">Bet while emotional or intoxicated</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 mt-1">✗</span>
                      <span className="text-gray-300">Ignore bankroll management rules</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 mt-1">✗</span>
                      <span className="text-gray-300">Rely solely on gut feelings</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Using PickStar AI */}
            <section id="how-to-use" className="bg-gradient-to-r from-emerald-500/10 to-violet-500/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-emerald-500/20" aria-labelledby="howto-heading">
              <h2 id="howto-heading" className="text-xl sm:text-2xl font-bold text-white mb-6">How to Use PickStar AI</h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-emerald-500/20 rounded-full p-2 flex-shrink-0">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Ask Specific Questions</h3>
                    <p className="text-gray-300">
                      "What's the best bet for tonight's Lakers game?" or "Show me value bets in NFL Week 10"
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-emerald-500/20 rounded-full p-2 flex-shrink-0">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Review AI Analysis</h3>
                    <p className="text-gray-300">
                      Our AI provides detailed reasoning, including stats, trends, and confidence levels
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-emerald-500/20 rounded-full p-2 flex-shrink-0">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Compare with Your Research</h3>
                    <p className="text-gray-300">
                      Use AI predictions alongside your own knowledge and other sources
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-emerald-500/20 rounded-full p-2 flex-shrink-0">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Place Smart Bets</h3>
                    <p className="text-gray-300">
                      Follow bankroll management rules and bet responsibly based on combined insights
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="faq-heading">
              <h2 id="faq-heading" className="text-xl sm:text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
              
              <div className="space-y-4">
                <div className="border-b border-white/10 pb-4">
                  <button
                    onClick={() => toggleSection('faq1')}
                    className="w-full text-left flex justify-between items-center hover:text-emerald-300 transition-colors"
                    aria-expanded={expandedSections.faq1}
                  >
                    <h3 className="font-semibold text-white">What makes PickStar AI better than ChatGPT for sports betting?</h3>
                    <span className="text-emerald-400">{expandedSections.faq1 ? '−' : '+'}</span>
                  </button>
                  {expandedSections.faq1 && (
                    <p className="mt-3 text-gray-300">
                      PickStar AI is built by machine learning engineers who are also professional sports bettors. Our specialized context engineering understands betting nuances that general AI models miss, providing superior predictions and real-time market intelligence.
                    </p>
                  )}
                </div>
                
                <div className="border-b border-white/10 pb-4">
                  <button
                    onClick={() => toggleSection('faq2')}
                    className="w-full text-left flex justify-between items-center hover:text-emerald-300 transition-colors"
                    aria-expanded={expandedSections.faq2}
                  >
                    <h3 className="font-semibold text-white">What is the 1-3% bankroll management rule?</h3>
                    <span className="text-emerald-400">{expandedSections.faq2 ? '−' : '+'}</span>
                  </button>
                  {expandedSections.faq2 && (
                    <p className="mt-3 text-gray-300">
                      The 1-3% rule means never risking more than 1-3% of your total bankroll on a single bet. For example, with a $1,000 bankroll, your maximum bet should be $10-$30 per wager. This protects you from devastating losses.
                    </p>
                  )}
                </div>
                
                <div className="border-b border-white/10 pb-4">
                  <button
                    onClick={() => toggleSection('faq3')}
                    className="w-full text-left flex justify-between items-center hover:text-emerald-300 transition-colors"
                    aria-expanded={expandedSections.faq3}
                  >
                    <h3 className="font-semibold text-white">What are the main types of sports bets?</h3>
                    <span className="text-emerald-400">{expandedSections.faq3 ? '−' : '+'}</span>
                  </button>
                  {expandedSections.faq3 && (
                    <p className="mt-3 text-gray-300">
                      The main bet types are: Moneyline (pick the winner), Spread (bet on margin of victory), Over/Under (total points scored), Props (specific player/game events), and Parlays (multiple bets combined for higher payouts).
                    </p>
                  )}
                </div>
                
                <div className="pb-4">
                  <button
                    onClick={() => toggleSection('faq4')}
                    className="w-full text-left flex justify-between items-center hover:text-emerald-300 transition-colors"
                    aria-expanded={expandedSections.faq4}
                  >
                    <h3 className="font-semibold text-white">How do American odds work?</h3>
                    <span className="text-emerald-400">{expandedSections.faq4 ? '−' : '+'}</span>
                  </button>
                  {expandedSections.faq4 && (
                    <p className="mt-3 text-gray-300">
                      American odds show potential profit. Positive odds (+150) mean bet $100 to win $150. Negative odds (-150) mean bet $150 to win $100. The favorites have negative odds, underdogs have positive odds.
                    </p>
                  )}
                </div>
              </div>
            </section>
            
            {/* CTA Section */}
            <section className="text-center py-8" aria-label="Call to action">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">Ready to Start Winning?</h2>
              <p className="text-gray-300 mb-6">
                Join thousands of smart bettors using AI to make better decisions
              </p>
              <Link 
                to="/chat"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-violet-500 text-white px-8 py-3 rounded-lg font-semibold hover:from-emerald-600 hover:to-violet-600 transition-all"
              >
                Start Chatting with AI
              </Link>
            </section>
          </div>
        </main>

        {/* Footer */}
        <SEOFooter language="en" />
      </div>
    </>
  );
};

export default SportsBettingGuide;